import axiosApi from "api/root.api";

export const MAX_IMAGE_BYTES = 100000000;
export const MAX_IMAGE_COUNT = 150;
const states = new Set([
  "uploading",
  "sealing",
  "queued",
  "processing",
  "applied",
  "failed",
  "conflict",
]);

function uuid() {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = [...bytes].map((n) => n.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function createImageManifest(files) {
  if (!files.length || files.length > MAX_IMAGE_COUNT) throw new Error("IMAGE_COUNT");
  files.forEach((file) => {
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type))
      throw new Error("IMAGE_TYPE");
    if (!file.size || file.size > MAX_IMAGE_BYTES)
      throw Object.assign(new Error("IMAGE_SIZE"), { fileName: file.name });
  });
  const sorted = [...files].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { numeric: true }),
  );
  return { batchId: uuid(), items: sorted.map((file) => ({ id: uuid(), file })) };
}

export function batchErrorMessage(error, t) {
  const code = error?.response?.data?.errorCode || error?.response?.data?.code || error?.message;
  return t(`scankitNew.batch.errors.${code}`, {
    defaultValue: t("scankitNew.batch.errors.UNKNOWN", {
      code: code || error?.response?.status || "",
    }),
    fileName: error?.fileName,
    status: error?.status,
  });
}

export function createImageBatchClient({ projectId, modelId, token, signal }) {
  const base = `/scan/editor/project/v1/${encodeURIComponent(projectId)}/${encodeURIComponent(modelId)}/image-batches`;
  const config = {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    signal,
    timeout: 30000,
  };
  const read = (response) => {
    const batch = response.data;
    if (
      !batch ||
      !states.has(batch.state) ||
      typeof batch.batchId !== "string" ||
      !Number.isInteger(batch.totalCount) ||
      !Number.isInteger(batch.processedCount)
    ) {
      throw new Error("INVALID_RESPONSE");
    }
    return batch;
  };
  const retryNetwork = async (request) => {
    for (let attempt = 0; ; attempt += 1) {
      try {
        const response = await request();
        return response;
      } catch (error) {
        if (
          signal.aborted ||
          attempt >= 2 ||
          error.response ||
          (!["ECONNABORTED", "ETIMEDOUT", "ERR_NETWORK"].includes(error.code) &&
            error.message !== "Network Error")
        )
          throw error;
      }
    }
  };
  return {
    create: ({ batchId, items }) =>
      retryNetwork(async () =>
        read(
          await axiosApi.post(
            base,
            {
              batchId,
              files: items.map(({ id, file }) => ({
                id,
                name: file.name,
                bytes: file.size,
                contentType: file.type,
              })),
            },
            config,
          ),
        ),
      ),
    prepare: (batchId, ids) =>
      retryNetwork(() =>
        axiosApi.post(`${base}/${encodeURIComponent(batchId)}/prepare-upload`, { ids }, config),
      ),
    uploadUrls: async (batchId, ids) =>
      (await axiosApi.post(`${base}/${encodeURIComponent(batchId)}/upload-urls`, { ids }, config))
        .data,
    complete: (batchId) =>
      retryNetwork(async () =>
        read(
          await axiosApi.post(
            `${base}/${encodeURIComponent(batchId)}/complete-upload`,
            undefined,
            config,
          ),
        ),
      ),
  };
}

function validUploadTarget(target, file) {
  try {
    const url = new URL(target.url);
    return (
      url.protocol === "https:" &&
      !url.username &&
      !url.password &&
      !url.port &&
      !url.hash &&
      /^(?:[a-z0-9.-]+\.)?s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/.test(url.hostname) &&
      url.searchParams.has("X-Amz-Signature") &&
      target.headers?.["Content-Type"] === file.type
    );
  } catch {
    return false;
  }
}

function putOriginal(target, file, signal, onProgress) {
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(new DOMException("Aborted", "AbortError"));
    const xhr = new XMLHttpRequest();
    const cancel = () => xhr.abort();
    const finish = (error) => {
      signal.removeEventListener("abort", cancel);
      if (error) reject(error);
      else resolve();
    };
    xhr.open("PUT", target.url);
    xhr.withCredentials = false;
    xhr.timeout = 120000;
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.upload.onprogress = (event) => onProgress(Math.min(event.loaded, file.size));
    xhr.onload = () => {
      // XHR follows redirects; only accept the original trusted S3 signed endpoint response.
      if (xhr.responseURL !== target.url) return finish(new Error("INVALID_TARGET"));
      if (xhr.status < 200 || xhr.status >= 300)
        return finish(
          Object.assign(new Error("UPLOAD_FAILED"), { status: xhr.status, fileName: file.name }),
        );
      onProgress(file.size);
      finish();
    };
    xhr.onerror = () => finish(Object.assign(new Error("UPLOAD_FAILED"), { fileName: file.name }));
    xhr.ontimeout = () =>
      finish(Object.assign(new Error("UPLOAD_TIMEOUT"), { fileName: file.name }));
    xhr.onabort = () => finish(new DOMException("Aborted", "AbortError"));
    signal.addEventListener("abort", cancel, { once: true });
    xhr.send(file);
  });
}

export async function uploadImageBatch({ client, manifest, signal, onProgress }) {
  const batch = await client.create(manifest);
  if (batch.state !== "uploading") return client.complete(batch.batchId);
  const totalBytes = manifest.items.reduce((sum, { file }) => sum + file.size, 0);
  const uploadedBytes = new Map();
  const progress = (item, loaded) => {
    uploadedBytes.set(item.id, loaded);
    const bytes = [...uploadedBytes.values()].reduce((sum, value) => sum + value, 0);
    onProgress(Math.min(100, Math.round((bytes / totalBytes) * 100)));
  };
  const targetsFor = async (items) => {
    const uploads = await client.uploadUrls(
      batch.batchId,
      items.map(({ id }) => id),
    );
    const targets = new Map((uploads || []).map((upload) => [upload.id, upload]));
    items.forEach(({ id, file }) => {
      const target = targets.get(id);
      if (!target || !validUploadTarget(target, file)) throw new Error("INVALID_TARGET");
    });
    return targets;
  };
  const uploadItem = async (item, targets) => {
    try {
      await putOriginal(targets.get(item.id), item.file, signal, (loaded) =>
        progress(item, loaded),
      );
    } catch (error) {
      if (error.status !== 403 || signal.aborted) throw error;
      const renewed = await targetsFor([item]);
      progress(item, 0);
      await putOriginal(renewed.get(item.id), item.file, signal, (loaded) =>
        progress(item, loaded),
      );
    }
    await client.prepare(batch.batchId, [item.id]);
  };
  for (let next = 0; next < manifest.items.length; next += 3) {
    if (signal.aborted) throw new DOMException("Aborted", "AbortError");
    const items = manifest.items.slice(next, next + 3);
    const targets = await targetsFor(items);
    const results = await Promise.allSettled(items.map((item) => uploadItem(item, targets)));
    if (signal.aborted) throw new DOMException("Aborted", "AbortError");
    const failure = results.find((result) => result.status === "rejected");
    if (failure)
      throw failure.reason instanceof Error ? failure.reason : new Error("UPLOAD_FAILED");
  }
  return client.complete(manifest.batchId);
}
